import { useCallback, useMemo, useState } from 'react'
import type { ChangeEvent } from 'react'
import { Experiments } from 'constants/experiments'
import useExperiment from 'hooks/Experiments/useExperiment'
import {
  groupAirportTransferPackageOptions,
  type AirportTransferPackageOptionView,
} from 'lib/offer/airportTransferPackageGrouping'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'

const EXCLUDED_OFFER_IDS = new Set([
  '006RF00000ZlOacYAF',
  '006RF00000cYSX5YAO',
])

interface AirportTransferPackageSelection {
  airportTransferBasePackageKey?: string
  airportTransferSelected?: boolean
  selectedPackageView: App.OfferPackageOptionView
}

export function useAirportTransferPackageOptions(
  basePackageViews: Array<App.OfferPackageOptionView>,
) {
  const offerId = basePackageViews[0]?.package.offerId
  const airportTransferGroupedPackages = useMemo(
    () => groupAirportTransferPackageOptions(basePackageViews),
    [basePackageViews],
  )
  const showAirportTransferOption = useMemo(
    () => airportTransferGroupedPackages.some(view => !!view.airportTransferPackageOption),
    [airportTransferGroupedPackages],
  )
  const { isAnyVariant: showAirportTransferPackageTickbox } = useExperiment(Experiments.croFlashPackageTickbox, {
    canActivate: showAirportTransferOption && !EXCLUDED_OFFER_IDS.has(offerId ?? ''),
  })

  return useMemo<Array<AirportTransferPackageOptionView>>(() => {
    if (showAirportTransferPackageTickbox) {
      return airportTransferGroupedPackages
    }
    return basePackageViews
  }, [airportTransferGroupedPackages, basePackageViews, showAirportTransferPackageTickbox])
}

export function useAirportTransferPackageSelection(
  rooms: Array<OfferPageRoom>,
  selectedPackageKey?: string,
) {
  const [airportTransferSelectionOverrides, setAirportTransferSelectionOverrides] = useState<Map<string, boolean>>(() => new Map())

  const isPackageViewSelected = useCallback((packageView: App.OfferPackageOptionView) => {
    return selectedPackageKey === packageView.package.uniqueKey || rooms.some(room =>
      packageView.package.id === room.roomId && packageView.package.roomRate.id === room.rateId,
    )
  }, [rooms, selectedPackageKey])

  const getPackageSelection = useCallback((view: AirportTransferPackageOptionView): AirportTransferPackageSelection => {
    const airportTransferPackageOption = view.airportTransferPackageOption
    const airportTransferBasePackageKey = airportTransferPackageOption?.basePackageView.package.uniqueKey
    const persistedAirportTransferSelected = airportTransferPackageOption ? isPackageViewSelected(airportTransferPackageOption.transferPackageView) : false
    const airportTransferSelectionOverride = airportTransferBasePackageKey ? airportTransferSelectionOverrides.get(airportTransferBasePackageKey) : undefined
    const airportTransferSelected = airportTransferSelectionOverride ?? (!!airportTransferBasePackageKey && persistedAirportTransferSelected)
    const selectedPackageView = airportTransferPackageOption && airportTransferSelected ? airportTransferPackageOption.transferPackageView : view

    return {
      airportTransferBasePackageKey,
      airportTransferSelected,
      selectedPackageView,
    }
  }, [airportTransferSelectionOverrides, isPackageViewSelected])

  const isRenderedPackageViewSelected = useCallback((view: AirportTransferPackageOptionView) => {
    const { selectedPackageView } = getPackageSelection(view)
    return isPackageViewSelected(selectedPackageView)
  }, [getPackageSelection, isPackageViewSelected])

  const onAirportTransferToggle = useCallback((basePackageKey: string, event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.currentTarget.checked

    setAirportTransferSelectionOverrides(previousOverrides => {
      const nextOverrides = new Map(previousOverrides)
      nextOverrides.set(basePackageKey, checked)
      return nextOverrides
    })
  }, [])

  return {
    getPackageSelection,
    isPackageViewSelected,
    isRenderedPackageViewSelected,
    onAirportTransferToggle,
  }
}
